import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("The new passwords do not match.");
      return;
    }
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refreshUser();
      toast.success("Password updated");
      navigate("/");
    } catch (err) {
      setError(errText(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white">
            <KeyRound className="h-5 w-5" />
          </div>
          <span className="font-head text-lg font-600 text-slate-900">Set your password</span>
        </div>

        {forced && (
          <div
            className="mb-5 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700"
            data-testid="force-change-banner"
          >
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            For your security, please choose a new password before continuing.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5">
            <Label htmlFor="current">{forced ? "Temporary password" : "Current password"}</Label>
            <Input
              id="current"
              type="password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">New password</Label>
            <Input
              id="new"
              type="password"
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="new-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="confirm-password"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" data-testid="change-password-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit"
            className="w-full bg-blue-600 text-white transition-colors hover:bg-blue-700"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </form>

        <button
          onClick={logout}
          className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600"
          data-testid="change-password-signout"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
